import { config } from '../../lib/config';
import type { Envelope, EnvelopeError, EnvelopePagination } from './types';

const CSRF_COOKIE = 'XSRF-TOKEN';
const CSRF_HEADER = 'X-XSRF-TOKEN';
const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function readCookie(name: string): string | null {
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf('=');
    if (eq < 0) continue;
    if (trimmed.slice(0, eq) === name) {
      return decodeURIComponent(trimmed.slice(eq + 1));
    }
  }
  return null;
}

/** Прикладная ошибка API: статус + тип/сообщение из конверта error. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly type: string,
    message: string,
    readonly eventCode?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

export interface RequestOptions {
  method?: string;
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  /** Для мутаций — отправить Idempotency-Key (ADR-013). По умолчанию true для мутирующих. */
  idempotent?: boolean;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${config.apiBase}${path}`;
  if (!query) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null) qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${url}?${s}` : url;
}

async function ensureCsrf(): Promise<string | null> {
  let token = readCookie(CSRF_COOKIE);
  if (!token) {
    await fetch(`${config.apiBase}/api/v1/csrf`, { credentials: 'include' });
    token = readCookie(CSRF_COOKIE);
  }
  return token;
}

/** Низкоуровневый запрос: возвращает полный конверт (data + pagination). */
export async function requestEnvelope<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<Envelope<T>> {
  const method = (opts.method ?? 'GET').toUpperCase();
  const headers = new Headers({ Accept: 'application/json' });
  const mutating = MUTATING.has(method);

  if (mutating) {
    const token = await ensureCsrf();
    if (token) headers.set(CSRF_HEADER, token);
    if (opts.idempotent !== false) headers.set('Idempotency-Key', crypto.randomUUID());
  }
  if (opts.body !== undefined) headers.set('Content-Type', 'application/json');

  const res = await fetch(buildUrl(path, opts.query), {
    method,
    headers,
    credentials: 'include',
    signal: opts.signal,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  let parsed: Partial<Envelope<T>> | null = null;
  const text = await res.text();
  if (text) {
    try {
      parsed = JSON.parse(text) as Partial<Envelope<T>>;
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    const err: EnvelopeError | null = parsed?.error ?? null;
    throw new ApiError(
      res.status,
      err?.type ?? 'http_error',
      err?.message ?? `Запрос завершился со статусом ${String(res.status)}`,
      parsed?.meta?.eventCode,
    );
  }

  return {
    meta: parsed?.meta ?? ({} as Envelope<T>['meta']),
    data: parsed?.data as T,
    error: null,
    pagination: parsed?.pagination ?? null,
    links: parsed?.links ?? null,
  };
}

/** Запрос, возвращающий только data. */
export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const env = await requestEnvelope<T>(path, opts);
  return env.data;
}

export type { EnvelopePagination };
